import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Skinfill Bacio",
  "Product information",
  "Opening an account",
  "Training",
  "Clinic support",
  "Other",
];

const INITIAL = {
  name: "",
  clinic: "",
  profession: "",
  email: "",
  mobile: "",
  city: "",
  country: "Australia",
  interest: "Skinfill Bacio",
  message: "",
};

const inputCls =
  "w-full rounded-2xl border border-[#EADECE] bg-white/80 px-5 py-4 text-sm text-[#1C1417] placeholder-[#6E6266]/60 font-light outline-none focus:border-[#A31D4F] focus:ring-2 focus:ring-[#A31D4F]/15 transition-all duration-300";

const EnquiryForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not send enquiry", {
        description: "Please try again, or email info@dermalis.com.au directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="enquiry-form-container" className="bg-[#F4EBE6] py-24 sm:py-36">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center">
          <Reveal>
            <Eyebrow>Contact Dermalis</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-serif font-light text-[#1C1417] text-4xl sm:text-6xl tracking-tight mt-5 text-balance leading-[1.04]">
              Bring <span className="italic text-[#A31D4F]">Skinfill Bacio</span> to your clinic.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 text-sm sm:text-base font-light text-[#6E6266] max-w-xl mx-auto">
              For clinics and aesthetic professionals in Australia and New Zealand. Send an enquiry and the Dermalis
              team will respond.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          {sent ? (
            <div
              data-testid="enquiry-success-message"
              className="mt-12 rounded-[32px] bg-white/80 border border-[#EADECE] p-10 sm:p-16 text-center shadow-[0_40px_80px_-50px_rgba(163,29,79,0.25)]"
            >
              <CheckCircle2 size={44} className="mx-auto text-[#A31D4F]" strokeWidth={1.4} />
              <p className="font-serif text-2xl sm:text-3xl text-[#1C1417] mt-6 leading-snug max-w-lg mx-auto">
                Thank you. Your enquiry has been sent to{" "}
                <span className="italic text-[#A31D4F]">Dermalis Australia &amp; New Zealand.</span>
              </p>
              <p className="text-sm font-light text-[#6E6266] mt-3">Our team will contact you shortly.</p>
              <button
                data-testid="enquiry-send-another-button"
                onClick={() => setSent(false)}
                className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A31D4F] hover:text-[#86143E] underline underline-offset-8 decoration-[#E893AE]/60 transition-colors"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <form
              data-testid="enquiry-form"
              onSubmit={submit}
              className="mt-12 rounded-[32px] bg-white/80 border border-[#EADECE] p-6 sm:p-10 shadow-[0_40px_80px_-50px_rgba(163,29,79,0.25)]"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A31D4F] mb-3">
                I&apos;m interested in
              </p>
              <div className="flex flex-wrap gap-2.5 mb-8">
                {INTERESTS.map((i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`enquiry-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setForm((f) => ({ ...f, interest: i }))}
                    className={`rounded-full font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors duration-300 ${
                      form.interest === i
                        ? "bg-[#A31D4F] text-white border-[#A31D4F]"
                        : "bg-transparent text-[#1C1417] border-[#EADECE] hover:border-[#A31D4F]/50"
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="enquiry-input-name" required placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="enquiry-input-clinic" placeholder="Clinic / Business" value={form.clinic} onChange={set("clinic")} className={inputCls} />
                <input data-testid="enquiry-input-profession" placeholder="Profession" value={form.profession} onChange={set("profession")} className={inputCls} />
                <input data-testid="enquiry-input-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                <input data-testid="enquiry-input-phone" placeholder="Mobile" value={form.mobile} onChange={set("mobile")} className={inputCls} />
                <input data-testid="enquiry-input-city" placeholder="City" value={form.city} onChange={set("city")} className={inputCls} />
                <select data-testid="enquiry-select-country" value={form.country} onChange={set("country")} className={`${inputCls} sm:col-span-2`}>
                  <option>Australia</option>
                  <option>New Zealand</option>
                </select>
              </div>
              <textarea
                data-testid="enquiry-input-message"
                placeholder="Message"
                rows={4}
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} mt-4 resize-none`}
              />

              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={sending}
                className="btn-lux btn-lux-magenta mt-8 w-full sm:w-auto justify-center disabled:opacity-60"
              >
                <span>{sending ? "Sending…" : "Send to Dermalis"}</span>
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default EnquiryForm;
